import { useCallback } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';

import { bundlesEndpoints as endpoints } from '@/api/endpoints';
import { callDelete, callGet, callPost, callPut } from '@/api/fetcher';
import { createEntityCache } from '@/hooks/utils/entityCache';
import { Bundle, Bundles } from '@/modules/bundles/models';
import { popModal } from '@/redux/modals/actions';
import { exportBundle as downloadBundle } from '@/util/fileManager';

import { revertDirty } from '../redux/actions';

export const API = Object.freeze({
  listBundles: async (deskId: string): Promise<Bundles> => callGet(endpoints.bundles(deskId)),

  getBundle: async (deskId: string, bundleId: string): Promise<Bundle> =>
    callGet(endpoints.bundle(deskId, bundleId)),

  addBundle: async (deskId: string, newBundle: Partial<Bundle>): Promise<Bundle> =>
    callPost(endpoints.bundles(deskId), newBundle),

  updateBundle: async (deskId: string, bundle: Partial<Bundle>): Promise<Bundle> =>
    callPut(endpoints.bundle(deskId, bundle.bundle_id), bundle),

  deleteBundle: async (deskId: string, bundleId: string): Promise<Bundle> =>
    callDelete(endpoints.bundle(deskId, bundleId)),
});

export const bundleCache = createEntityCache<Bundle, 'bundles'>({
  listKey: (deskId) => [endpoints.bundles(deskId)],
  detailKey: (deskId, bundleId) => [endpoints.bundle(deskId, bundleId)],
  listField: 'bundles',
  getId: (bundle) => bundle.bundle_id,
  getParentId: (bundle) => bundle.desk_id,
});

export const useBundles = (deskId: string, bundleId?: string) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { data: bundles, status: listStatus } = useQuery<Bundles, Error>({
    queryKey: [endpoints.bundles(deskId)],
    queryFn: () => API.listBundles(deskId),
    enabled: !!deskId,
  });

  const {
    data: bundle,
    isSuccess,
    isLoading,
  } = useQuery<Bundle, Error>({
    queryKey: [endpoints.bundle(deskId, bundleId)],
    queryFn: () => API.getBundle(deskId, bundleId),
    enabled: !!bundleId && !!deskId && bundleId !== 'draft',
  });

  const { mutate: updateBundle, status: updateStatus } = useMutation<
    Bundle,
    Error,
    Partial<Bundle>
  >({
    mutationFn: (bundle) => API.updateBundle(deskId, bundle),
    onSuccess: (resp) => {
      dispatch(revertDirty({ dirtyState: false }));
      bundleCache.updated(queryClient, resp, { parentId: deskId });
      toast.success(t('bundles.updated', { 0: resp.name }));
    },
    meta: { errorToast: true },
  });

  const { mutate: addBundle, status: createStatus } = useMutation<Bundle, Error, Partial<Bundle>>({
    mutationFn: (bundle) => API.addBundle(deskId, bundle),
    onSuccess: (resp) => {
      dispatch(revertDirty({ dirtyState: false }));
      bundleCache.created(queryClient, resp, { parentId: deskId });
      toast.success(t('bundles.created', { 0: resp.name }));
    },
    meta: { errorToast: true },
  });

  const { mutate: deleteBundle, status: deleteStatus } = useMutation<Bundle, Error, string>({
    mutationFn: (id) => API.deleteBundle(deskId, id),
    onSuccess: (resp) => {
      dispatch(popModal());
      bundleCache.removed(queryClient, { parentId: deskId, id: resp.bundle_id });
      toast.success(t('bundles.deleted'));
    },
    meta: { errorToast: true },
  });

  const exportBundle = async (desk_id: string, bundle_id: string) => {
    const eBundle = await callGet(`/www/api/v1/desks/${desk_id}/bundles/${bundle_id}`);
    downloadBundle(eBundle);
    toast.success(t('bundles.exported', { 0: eBundle.name }));
  };

  const getAllBundlesFromDeskIds = useCallback(async (deskIds: string[]) => {
    const responses = await Promise.all(deskIds.map((deskId) => API.listBundles(deskId)));

    const allBundles = responses.map((r) => r?.bundles).flat();

    return allBundles;
  }, []);

  return {
    bundles: bundles?.bundles,
    getAllBundlesFromDeskIds: getAllBundlesFromDeskIds,
    bundle,
    isSuccess,
    listStatus,
    updateBundle,
    updateStatus,
    addBundle,
    createStatus,
    deleteBundle,
    deleteStatus,
    exportBundle,
    isLoading,
  };
};
